import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Edit, Trash2, Save, XCircle, AlertCircle, CheckCircle, Upload, Image as ImageIcon } from 'lucide-react';
import { supabase } from '../../utils/supabase';
import { useSupabaseCache } from '../../hooks/useSupabaseCache';
import LoadingSpinner from '../common/LoadingSpinner';

const BannersManagement = () => {
  const {
    data: banners,
    loading,
    error,
    refresh,
    addItem,
    updateItem,
    removeItem
  } = useSupabaseCache('banners', {
    orderBy: 'order_index',
    ascending: true,
    limit: 30
  });

  const [editingBanner, setEditingBanner] = useState(null);
  const [form, setForm] = useState({
    title: '', subtitle: '', link_url: '', image_url: '', order_index: 0, is_active: true
  });
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(false);

  const RECOMMENDED_DIMENSIONS = "1920x600px (relación de aspecto 16:5)";


  const ensureAuthentication = async () => {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      console.log('No hay usuario autenticado, intentando autenticación automática...');

      const adminEmail = process.env.REACT_APP_ADMIN_EMAIL || 'admin@clubmia.com';
      const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD || 'admin123';

      const { data, error } = await supabase.auth.signInWithPassword({
        email: adminEmail,
        password: adminPassword,
      });

      if (error) {
        console.error('Error en autenticación automática:', error);
        return null;
      }

      console.log('Autenticación automática exitosa:', data.user);
      return data.user;
    }

    return user;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('🖼️ Iniciando subida de imagen:', file.name);
    setUploading(true);
    setMessage(null);

    try {
      const filePath = `public/banners/${Date.now()}_${file.name}`;
      const bucketName = 'images-club-mia';

      console.log('📁 Subiendo a:', bucketName, filePath);

      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        console.error('❌ Error uploading image:', error);
        setMessage({ type: 'error', text: 'Error al subir la imagen: ' + error.message });
        return;
      }

      console.log('✅ Imagen subida exitosamente:', data);

      const publicURL = supabase.storage.from(bucketName).getPublicUrl(filePath).data.publicUrl;
      console.log('🔗 URL pública generada:', publicURL);

      // Actualizar el formulario con la nueva URL
      setForm((prevForm) => {
        const newForm = { ...prevForm, image_url: publicURL };
        console.log('📝 Formulario actualizado:', newForm);
        return newForm;
      });

      // Si estamos editando un banner existente, actualizar la DB inmediatamente
      if (editingBanner) {
        console.log('🔄 Actualizando URL de imagen en la DB para banner existente...');
        try {
          const { data, error } = await supabase
            .from('banners')
            .update({ image_url: publicURL })
            .eq('id', editingBanner.id)
            .select();

          if (error) {
            console.error('❌ Error actualizando imagen en DB:', error);
            setMessage({ type: 'warning', text: 'Imagen subida, pero error al actualizar en la base de datos: ' + error.message });
          } else {
            console.log('✅ URL de imagen actualizada en DB:', data[0]);
            // Actualizar el item en la lista
            updateItem(editingBanner.id, data[0]);
            setMessage({ type: 'success', text: 'Imagen subida y actualizada en la base de datos automáticamente.' });
          }
        } catch (dbError) {
          console.error('💥 Error inesperado actualizando DB:', dbError);
          setMessage({ type: 'warning', text: 'Imagen subida, pero error inesperado al actualizar la base de datos.' });
        }
      } else {
        setMessage({ type: 'success', text: 'Imagen subida con éxito. Recuerda guardar el banner para completar la creación.' });
      }

    } catch (error) {
      console.error('💥 Error inesperado:', error);
      setMessage({ type: 'error', text: 'Error inesperado al subir la imagen: ' + error.message });
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setForm({
      title: banner.title,
      subtitle: banner.subtitle || '',
      link_url: banner.link_url || '',
      image_url: banner.image_url || '',
      order_index: banner.order_index,
      is_active: banner.is_active
    });
    setMessage(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este banner?')) return;

    try {
      const { error } = await supabase
        .from('banners')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting banner:', error);
        setMessage({ type: 'error', text: `Error al eliminar el banner: ${error.message}` });
      } else {
        removeItem(id);
        setMessage({ type: 'success', text: 'Banner eliminado con éxito.' });
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      setMessage({ type: 'error', text: `Error inesperado: ${error.message}` });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    // Validación
    if (!form.title.trim()) {
      setMessage({ type: 'error', text: 'El título es obligatorio.' });
      return;
    }

    // Verificar/obtener autenticación
    const user = await ensureAuthentication();
    if (!user) {
      setMessage({ type: 'error', text: 'Error de autenticación. Verifica la configuración.' });
      return;
    }

    // Crear payload limpio sin campos que puedan causar problemas con RLS
    const payload = {
      title: form.title,
      subtitle: form.subtitle || '',
      link_url: form.link_url || '',
      image_url: form.image_url || '',
      order_index: parseInt(form.order_index, 10) || 0,
      is_active: form.is_active !== undefined ? form.is_active : true,
    };

    // Solo agregar created_by si el usuario está autenticado
    if (user && user.id) {
      payload.created_by = user.id;
    }

    console.log('🚀 Payload to insert/update:', payload);
    console.log('👤 Current user:', user);
    console.log('📋 Current form state:', form);
    console.log('🖼️ Image URL in payload:', payload.image_url);

    try {
      if (editingBanner) {
        const { data, error } = await supabase
          .from('banners')
          .update(payload)
          .eq('id', editingBanner.id)
          .select();

        if (error) {
          console.error('❌ Error updating banner:', error);
          console.error('Error details:', error);
          setMessage({ type: 'error', text: `Error al actualizar el banner: ${error.message}` });
        } else {
          console.log('✅ Banner actualizado exitosamente:', data[0]);
          console.log('🖼️ URL de imagen guardada:', data[0].image_url);
          updateItem(editingBanner.id, data[0]);
          setMessage({ type: 'success', text: 'Banner actualizado con éxito.' });
          setEditingBanner(null);
          setForm({ title: '', subtitle: '', link_url: '', image_url: '', order_index: 0, is_active: true });
        }
      } else {
        const { data, error } = await supabase
          .from('banners')
          .insert([payload])
          .select();

        if (error) {
          console.error('❌ Error adding banner:', error);
          console.error('Error details:', error);
          console.error('Error code:', error.code);
          console.error('Error hint:', error.hint);
          setMessage({ type: 'error', text: `Error al añadir el banner: ${error.message}. Código: ${error.code}` });
        } else {
          console.log('✅ Banner añadido exitosamente:', data[0]);
          console.log('🖼️ URL de imagen guardada:', data[0].image_url);
          addItem(data[0]);
          setMessage({ type: 'success', text: 'Banner añadido con éxito.' });
          setForm({ title: '', subtitle: '', link_url: '', image_url: '', order_index: 0, is_active: true });
        }
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      setMessage({ type: 'error', text: `Error inesperado: ${error.message}` });
    }
  };

  const handleCancelEdit = () => {
    setEditingBanner(null);
    setForm({ title: '', subtitle: '', link_url: '', image_url: '', order_index: 0, is_active: true });
    setMessage(null);
  };

  if (loading) return <div className="p-8 text-center text-gray-600">Cargando banners...</div>;
  if (error) return <div className="p-8 text-center text-red-600">{error}</div>;

  return (
    <motion.div
      className="p-8 bg-gray-50 min-h-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-4xl font-bold text-gray-800">Gestión de Banners</h2>
        <div className="flex gap-2">
          <button
            onClick={refresh}
            disabled={loading}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            Actualizar
          </button>

          <button
            onClick={async () => {
              const user = await ensureAuthentication();
              if (user) {
                setMessage({ type: 'success', text: `Autenticado como: ${user.email || user.id}` });
              } else {
                setMessage({ type: 'error', text: 'Error de autenticación' });
              }
            }}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            Verificar Auth
          </button>
        </div>
      </div>

      {/* Formulario de Añadir/Editar Banner */}
      <motion.div
        className="bg-white rounded-xl shadow-md p-6 mb-8"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <h3 className="text-2xl font-bold text-gray-800 mb-4">
          {editingBanner ? 'Editar Banner' : 'Añadir Nuevo Banner'}
        </h3>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="text"
            name="title"
            placeholder="Título del banner"
            value={form.title}
            onChange={handleInputChange}
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            required
          />
          <input
            type="text"
            name="subtitle"
            placeholder="Subtítulo (opcional)"
            value={form.subtitle}
            onChange={handleInputChange}
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <input
            type="url"
            name="link_url"
            placeholder="URL de enlace (opcional)"
            value={form.link_url}
            onChange={handleInputChange}
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <input
            type="number"
            name="order_index"
            placeholder="Orden"
            value={form.order_index}
            onChange={handleInputChange}
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            required
          />

          {/* Campo de subida de imagen */}
          <div className="md:col-span-2">
            <label htmlFor="image-upload" className="block text-sm font-medium text-gray-700 mb-2">
              Imagen del Banner ({RECOMMENDED_DIMENSIONS})
            </label>
            <div className="flex items-center space-x-4">
              <input
                type="file"
                id="image-upload"
                accept="image/*"
                onChange={handleImageUpload}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-orange-50 file:text-orange-700
                  hover:file:bg-orange-100"
                disabled={uploading}
              />
              {uploading && (
                <svg className="animate-spin h-5 w-5 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
            </div>
            {form.image_url && (
              <div className="mt-4">
                <div className="flex items-center space-x-2 mb-2">
                  <ImageIcon className="w-5 h-5 text-gray-500" />
                  <a href={form.image_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate">
                    {form.image_url.split('/').pop()}
                  </a>
                </div>
                <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                  <img
                    src={form.image_url}
                    alt="Vista previa"
                    className="max-w-full h-32 object-contain mx-auto"
                    onError={(e) => {
                      console.log('❌ Error cargando imagen de vista previa:', form.image_url);
                      e.target.style.display = 'none';
                    }}
                    onLoad={() => {
                      console.log('✅ Imagen de vista previa cargada correctamente:', form.image_url);
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Checkbox de Activo */}
          <div className="md:col-span-2 flex items-center">
            <input
              type="checkbox"
              name="is_active"
              id="is_active"
              checked={form.is_active}
              onChange={handleInputChange}
              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
              Activo
            </label>
          </div>

          <div className="md:col-span-2 flex justify-end space-x-4 mt-4">
            {editingBanner && (
              <motion.button
                type="button"
                onClick={handleCancelEdit}
                className="flex items-center px-6 py-3 bg-gray-300 text-gray-800 rounded-xl font-semibold hover:bg-gray-400 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <XCircle className="w-5 h-5 mr-2" /> Cancelar
              </motion.button>
            )}
            <motion.button
              type="submit"
              className="flex items-center px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Save className="w-5 h-5 mr-2" /> {editingBanner ? 'Actualizar Banner' : 'Añadir Banner'}
            </motion.button>
          </div>
        </form>
        <AnimatePresence>
          {message && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`mt-4 p-3 rounded-xl flex items-center gap-2 ${
                message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}
            >
              {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              {message.text}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Lista de Banners */}
      {loading ? (
        <LoadingSpinner text="Cargando banners..." />
      ) : error ? (
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={refresh}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Reintentar
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {Array.isArray(banners) && banners.map((banner) => (
            <motion.div
              key={banner.id}
              layout
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.2 } }}
              transition={{ type: "spring", stiffness: 100, damping: 10, duration: 0.5 }}
              className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100"
            >
              <img src={banner.image_url} alt={banner.title} className="w-full h-48 object-cover" />
              <div className="p-4">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{banner.title}</h3>
                <p className="text-gray-600 text-sm mb-2 line-clamp-2">{banner.subtitle}</p>
                <div className="flex items-center text-gray-500 text-sm mb-2">
                  <ImageIcon className="w-4 h-4 mr-2" />
                  <span>Orden: {banner.order_index}</span>
                </div>
                <div className="flex items-center text-gray-500 text-sm mb-2">
                  {banner.is_active ? (
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500 mr-2" />
                  )}
                  <span>{banner.is_active ? 'Activo' : 'Inactivo'}</span>
                </div>
                <div className="flex justify-end space-x-2 mt-4">
                  <motion.button
                    onClick={() => handleEdit(banner)}
                    className="p-2 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Edit className="w-5 h-5" />
                  </motion.button>
                  <motion.button
                    onClick={() => handleDelete(banner.id)}
                    className="p-2 bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Trash2 className="w-5 h-5" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
};

export default BannersManagement;